import { CalendarX, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'

import {
  useCancelAppointment,
  type Appointment,
} from '@/api/appointments'

interface AppointmentCancelDialogProps {
  open: boolean
  onClose: () => void
  companyId: number | undefined
  appointment: Appointment | null
}

// Garde l'esthétique de CalendarsView.tsx (bandeau coloré + ring autour de l'icône).
export function AppointmentCancelDialog({
  open,
  onClose,
  companyId,
  appointment,
}: AppointmentCancelDialogProps) {
  const { t, i18n } = useTranslation()
  const dateLocale = i18n.language?.startsWith('en') ? 'en-CA' : 'fr-CA'
  const cancelMutation = useCancelAppointment(companyId)

  const handleConfirm = async () => {
    if (!appointment) return
    try {
      await cancelMutation.mutateAsync(appointment.id)
      toast.success(t('appointments.cancelSuccess'))
      onClose()
    } catch {
      toast.error(t('appointments.cancelError'))
    }
  }

  const formatDateTime = (iso: string) =>
    new Date(iso).toLocaleString(dateLocale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-md border-0 p-0 shadow-2xl overflow-hidden"
      >
        <div className="h-1 w-full bg-gradient-to-r from-rose-500 via-rose-400 to-rose-500" />
        <div className="p-6 pb-0">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-rose-50 dark:bg-rose-950/30 ring-1 ring-rose-200 dark:ring-rose-900/50">
              <CalendarX className="h-5 w-5 text-rose-600 dark:text-rose-400" />
            </div>
            <div>
              <DialogTitle className="text-xl tracking-tight">
                {t('appointments.cancelDialogTitle')}
              </DialogTitle>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t('appointments.cancelConfirm')}
              </p>
            </div>
          </div>
        </div>

        {appointment && (
          <div className="mx-6 mt-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3">
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              {appointment.client_name}
            </p>
            <p className="text-xs text-slate-500">
              {formatDateTime(appointment.start_time)}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t bg-slate-50 dark:bg-slate-800/50 px-6 py-4 mt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={cancelMutation.isPending}
          >
            {t('appointments.formCancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void handleConfirm()}
            disabled={cancelMutation.isPending}
            className="gap-2"
          >
            {cancelMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CalendarX className="h-4 w-4" />
            )}
            {t('appointments.cancelDialogConfirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
